import React, { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  Alert,
  BrandColorInput,
  Button,
  Capacity,
  Card,
  Input,
  Switch,
  Tag,
} from '@rallyshift/design-system'
import api, { OrganizationSubscription } from '../../api/client'
import { useOrganization } from '../../contexts/OrganizationContext'
import {
  ORG_STATUSES,
  PLANS,
  SUB_STATUSES,
  formatSubscriptionDate,
  labelFor,
} from '../../utils/subscription'

const SUB_STATUS_STYLE: Record<string, string> = {
  trialing: 'bg-status-info-bg text-status-info-text',
  active: 'bg-status-success-bg text-status-success-text',
  past_due: 'bg-status-warning-bg text-status-warning-text',
  cancelled: 'bg-status-danger-bg text-status-danger-text',
}

const SubscriptionRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-2">
    <dt className="text-sm text-text-secondary">{label}</dt>
    <dd className="m-0 text-right text-sm font-semibold text-text-primary">{children}</dd>
  </div>
)

interface Skill {
  id: string
  name: string
  description: string | null
  sort_order: number
  archived: boolean
}

/**
 * Organisation settings, doubling as the branding step of onboarding (design screen 1i).
 * Volunteers see the organisation's logo and colour, not ours, so the live preview and
 * the contrast gate sit right next to the inputs that drive them.
 */
const OrgSettingsPage: React.FC = () => {
  const { organization, refresh } = useOrganization()

  const [name, setName] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [brandColor, setBrandColor] = useState('#C4502E')
  const [allowPublicSignup, setAllowPublicSignup] = useState(true)
  const [checkInEnabled, setCheckInEnabled] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null)

  const [skills, setSkills] = useState<Skill[]>([])
  const [newSkill, setNewSkill] = useState('')
  const [skillBusy, setSkillBusy] = useState(false)

  // The public branding endpoint withholds the contact address, so the form loads from
  // the admin view instead: saving blanks back would quietly break every reply-to.
  useEffect(() => {
    api.organizations
      .settings()
      .then((settings) => {
        setName(settings.name)
        setLogoUrl(settings.logo_url ?? '')
        setContactEmail(settings.contact_email ?? '')
        setBrandColor(settings.brand_color)
        setAllowPublicSignup(settings.allow_public_signup)
        setCheckInEnabled(settings.volunteer_check_in_enabled)
      })
      .catch((e: any) => setError(e.message || 'Instellingen konden niet worden geladen.'))
    // Informational only, so a failure here should not put an error over the whole form.
    api.organizations
      .subscription()
      .then(setSubscription)
      .catch(() => setSubscription(null))
  }, [])

  const loadSkills = useCallback(async () => {
    try {
      setSkills(await api.organizations.listSkills())
    } catch (e: any) {
      setError(e.message || 'Vaardigheden konden niet worden geladen.')
    }
  }, [])

  useEffect(() => {
    loadSkills()
  }, [loadSkills])

  const save = async () => {
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      await api.organizations.update({
        name,
        logo_url: logoUrl.trim() || null,
        contact_email: contactEmail.trim() || null,
        brand_color: brandColor,
      })
      await refresh()
      setSuccess('Je huisstijl is opgeslagen.')
    } catch (e: any) {
      setError(e.message || 'Opslaan is niet gelukt.')
    } finally {
      setSaving(false)
    }
  }

  /** A single switch with no form around it: save on flip, revert if that fails. */
  const setPublicSignup = async (next: boolean) => {
    setAllowPublicSignup(next)
    setError('')
    try {
      await api.organizations.update({ allow_public_signup: next })
      await refresh()
    } catch (e: any) {
      setAllowPublicSignup(!next)
      setError(e.message || 'Deze instelling opslaan is niet gelukt.')
    }
  }

  const setVolunteerCheckIn = async (next: boolean) => {
    setCheckInEnabled(next)
    setError('')
    try {
      await api.organizations.update({ volunteer_check_in_enabled: next })
      await refresh()
    } catch (e: any) {
      setCheckInEnabled(!next)
      setError(e.message || 'Deze instelling opslaan is niet gelukt.')
    }
  }

  const addSkill = async () => {
    const trimmed = newSkill.trim()
    if (!trimmed) return
    setSkillBusy(true)
    try {
      await api.organizations.createSkill({ name: trimmed })
      setNewSkill('')
      await loadSkills()
    } catch (e: any) {
      setError(e.message || 'Vaardigheid toevoegen is niet gelukt.')
    } finally {
      setSkillBusy(false)
    }
  }

  const removeSkill = async (id: string) => {
    setSkillBusy(true)
    try {
      await api.organizations.deleteSkill(id)
      await loadSkills()
    } catch (e: any) {
      setError(e.message || 'Vaardigheid verwijderen is niet gelukt.')
    } finally {
      setSkillBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-display-lg text-text-primary">
          Maak het van jouw evenement
        </h1>
        <p className="mt-2 text-md text-text-secondary">
          Vrijwilligers zien jouw logo en kleur — niet die van ons. Wij bewaken de
          leesbaarheid automatisch.
        </p>
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card title="Huisstijl">
            <div className="space-y-5">
              <Input
                label="Naam van de organisatie"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                label="Logo"
                hint="Plak de URL van je logo. Een liggend logo van ongeveer 56 pixels hoog werkt het beste."
                placeholder="https://…/logo.png"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
              />
              <Input
                label="Contactadres"
                hint="Hier komen antwoorden op je e-mails binnen."
                type="email"
                placeholder="hallo@jouwevenement.nl"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              <BrandColorInput
                label="Jouw kleur"
                value={brandColor}
                onChange={(brand) => setBrandColor(brand)}
              />
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={save} loading={saving}>
                Bewaar huisstijl
              </Button>
            </div>
          </Card>

          <Card title="Hoe komen vrijwilligers binnen?">
            <Switch
              checked={allowPublicSignup}
              onChange={setPublicSignup}
              label="Iedereen mag zich aanmelden"
              description={
                allowPublicSignup
                  ? `Wie ${window.location.host}/signup bezoekt kan zich aanmelden en wacht daarna op jouw goedkeuring.`
                  : 'Alleen mensen die jij uitnodigt kunnen een account maken.'
              }
            />
          </Card>

          <Card title="Op de dag zelf">
            <Switch
              checked={checkInEnabled}
              onChange={setVolunteerCheckIn}
              label="Vrijwilligers checken zelf in"
              description={
                checkInEnabled
                  ? 'Vrijwilligers zien de pagina Inchecken en melden zich daar bij aanvang van hun dienst.'
                  : 'De pagina Inchecken is verborgen. Jij registreert aanwezigheid via Evenementdag.'
              }
            />
          </Card>

          <Card
            title="Vaardigheden"
            meta="De lijst waaruit je kiest bij diensten en vrijwilligers."
          >
            <div className="flex flex-wrap gap-2">
              {skills.length === 0 ? (
                <p className="text-sm text-text-muted">Nog geen vaardigheden ingesteld.</p>
              ) : (
                skills.map((skill) => (
                  <Tag
                    key={skill.id}
                    onRemove={skillBusy ? undefined : () => removeSkill(skill.id)}
                    removeLabel={`Verwijder ${skill.name}`}
                  >
                    {skill.name}
                  </Tag>
                ))
              )}
            </div>

            <div className="mt-4 flex items-end gap-2">
              <Input
                label="Nieuwe vaardigheid"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addSkill()
                  }
                }}
                placeholder="Bijvoorbeeld: EHBO"
              />
              <Button
                variant="secondary"
                onClick={addSkill}
                loading={skillBusy}
                icon={<Plus className="h-4 w-4" />}
              >
                Voeg toe
              </Button>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          {subscription && (
            <Card
              title="Abonnement"
              meta="Beheerd door RallyShift. Wil je iets wijzigen? Neem contact met ons op."
              actions={
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    SUB_STATUS_STYLE[subscription.subscription_status] ??
                    'bg-surface-sunken text-text-secondary'
                  }`}
                >
                  {labelFor(SUB_STATUSES, subscription.subscription_status)}
                </span>
              }
            >
              <p className="font-display text-display-md text-action-primary">
                {labelFor(PLANS, subscription.plan)}
              </p>

              {subscription.status !== 'active' && (
                <div className="mt-3">
                  <Alert variant="warning">
                    Je organisatie is {labelFor(ORG_STATUSES, subscription.status).toLowerCase()}.
                  </Alert>
                </div>
              )}

              <dl className="mt-3 divide-y divide-border">
                {subscription.subscription_status === 'trialing' && (
                  <SubscriptionRow label="Proefperiode tot">
                    {formatSubscriptionDate(subscription.trial_ends_at)}
                  </SubscriptionRow>
                )}
                <SubscriptionRow label="Klant sinds">
                  {formatSubscriptionDate(
                    subscription.subscription_started_at ?? subscription.customer_since,
                  )}
                </SubscriptionRow>
                <SubscriptionRow label="Huidige periode">
                  {subscription.current_period_start || subscription.current_period_end
                    ? `${formatSubscriptionDate(subscription.current_period_start)} – ${formatSubscriptionDate(subscription.current_period_end)}`
                    : '—'}
                </SubscriptionRow>
                <SubscriptionRow label="Zitplaatsen">
                  {subscription.seat_limit == null
                    ? `${subscription.seats_used} in gebruik · onbeperkt`
                    : `${subscription.seats_used} van ${subscription.seat_limit}`}
                </SubscriptionRow>
              </dl>

              {subscription.seat_limit != null && (
                <Capacity
                  className="mt-3"
                  value={subscription.seats_used}
                  max={subscription.seat_limit}
                  label="Accounts"
                  stateLabels={{ open: 'ruimte', filling: 'raakt vol', full: 'vol', over: 'over limiet' }}
                />
              )}
            </Card>
          )}

          <div>
            <p className="mb-2 text-sm font-semibold text-text-muted">
              Live voorbeeld — aanmeldpagina
            </p>
            <div className="overflow-hidden rounded-lg border border-border bg-surface-card shadow-card">
              <div
                className="flex items-center justify-center p-6"
                style={{ background: brandColor }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt={name} className="h-14" />
                ) : (
                  <span className="font-display text-display-sm text-tenant-on-brand">
                    {name || 'Jouw evenement'}
                  </span>
                )}
              </div>
              <div className="p-8 text-center">
                <h2 className="font-display text-display-md text-text-primary">
                  Help mee met {name || 'jouw evenement'}
                </h2>
                <p className="mt-2 text-md text-text-secondary">
                  Kies zelf de diensten die je passen.
                </p>
                <div
                  className="mx-auto mt-6 w-full max-w-xs rounded-md px-6 py-4 text-md font-semibold"
                  style={{ background: brandColor, color: organization?.on_brand_color ?? '#FFFFFF' }}
                >
                  Meld je aan
                </div>
              </div>
              <div className="border-t border-border bg-surface-sunken px-6 py-4 text-center text-xs text-text-muted">
                mogelijk gemaakt door{' '}
                <span className="font-bold text-action-primary">RallyShift</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {skills.length > 0 && (
        <Card title="Vaardigheden beheren">
          <ul className="divide-y divide-border">
            {skills.map((skill) => (
              <li key={skill.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-md font-semibold text-text-primary">{skill.name}</p>
                  {skill.description && (
                    <p className="text-sm text-text-secondary">{skill.description}</p>
                  )}
                </div>
                <Button
                  variant="danger"
                  disabled={skillBusy}
                  onClick={() => removeSkill(skill.id)}
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                >
                  Verwijder
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

export default OrgSettingsPage
